import { Effect } from 'effect';
import {
  type OkraClientOptions,
  type OkraEffectClient,
  OkraClientError,
  runOkraClient,
} from './client-runtime.js';
import type { BillingUsage, CardSetupSession, TopupSession } from './billing.js';

/**
 * [A.6.5b] Typed `/v1/billing` client — promise facade over `HttpApiClient`.
 *
 * Follows the A.6.5a jobs facade exactly: consumers get one typed factory here
 * instead of re-declaring URLs, query strings, and response interfaces. The
 * wire shape (`BillingUsage`, from `./billing.js`) is decoded by the effect
 * client and then normalized into the dashboard's mutable-array view.
 */

export { OkraClientError as BillingClientError };

export interface BillingBalanceView {
  cents: number | null;
  dollars: number | null;
  updatedAt: string | null;
}

export interface BillingCreditView {
  lifetimePurchasedCents: number;
  lifetimeGrantedCents: number;
  lifetimeSpentCents: number;
  /** [A.6.8] Absent on servers that predate the card-verified signup credit. */
  signupCreditAvailable?: boolean;
}

export interface BillingTransactionView {
  id: string;
  productType: string;
  description: string | null;
  totalCostCents: number;
  status: string;
  createdAt: string;
}

export interface BillingUsageByEndpointView {
  endpoint: string;
  costUsd: number;
  requestCount: number;
}

export interface BillingUsageRecentView {
  endpoint: string;
  documentId: string | null;
  costUsd: number;
  vendor: string | null;
  model: string | null;
  settlementStatus?: string | null;
  deducted?: boolean | null;
  createdAt: string;
}

export interface BillingEdgeUsageView {
  totalCostUsd: number;
  requestCount: number;
  byEndpoint: BillingUsageByEndpointView[];
  recent: BillingUsageRecentView[];
}

/**
 * Normalized billing usage — the dashboard's view (was `api/types.ts`
 * `BillingUsageResponse`). Optional aggregates stay optional so existing
 * `usage.credit?.…` call sites keep their narrowing.
 */
export interface BillingUsageResponse {
  balance: BillingBalanceView;
  credit?: BillingCreditView;
  spend?: Record<string, number>;
  transactions?: BillingTransactionView[];
  usage: BillingEdgeUsageView;
}

export interface TopupSessionResult {
  sessionId: string;
  paymentStatus: string | null;
  amountTotalCents: number;
  bonusCents: number;
  totalCreditedCents: number;
  newBalanceCents: number;
  applied: boolean;
}

export interface CheckoutSessionResult {
  url: string;
}

export interface CardSetupSessionResult {
  sessionId: string;
  status: CardSetupSession['status'];
  grantedCents: number;
  newBalanceCents: number;
}

export interface BillingClient {
  getBillingUsage(): Promise<BillingUsageResponse>;
  createCheckoutSession(params: { amountCents: number }): Promise<CheckoutSessionResult>;
  getTopupSession(params: { sessionId: string }): Promise<TopupSessionResult>;
  createCardSetupSession(): Promise<CheckoutSessionResult>;
  getCardSetupSession(params: { sessionId: string }): Promise<CardSetupSessionResult>;
}

export interface BillingClientOptions extends OkraClientOptions {}

function normalizeUsage(wire: BillingUsage): BillingUsageResponse {
  return {
    balance: {
      cents: wire.balance.cents,
      dollars: wire.balance.dollars,
      updatedAt: wire.balance.updatedAt,
    },
    ...(wire.credit !== undefined ? { credit: { ...wire.credit } } : {}),
    ...(wire.spend !== undefined ? { spend: { ...wire.spend } } : {}),
    ...(wire.transactions !== undefined
      ? { transactions: wire.transactions.map((tx) => ({ ...tx })) }
      : {}),
    usage: {
      totalCostUsd: wire.usage.totalCostUsd,
      requestCount: wire.usage.requestCount,
      byEndpoint: wire.usage.byEndpoint.map((row) => ({ ...row })),
      recent: wire.usage.recent.map((row) => ({ ...row })),
    },
  };
}

function normalizeTopup(wire: TopupSession): TopupSessionResult {
  return {
    sessionId: wire.sessionId,
    paymentStatus: wire.paymentStatus,
    amountTotalCents: wire.amountTotalCents,
    bonusCents: wire.bonusCents,
    totalCreditedCents: wire.totalCreditedCents,
    newBalanceCents: wire.newBalanceCents,
    applied: wire.applied,
  };
}

export function makeBillingClient(options: BillingClientOptions): BillingClient {
  const run = <A>(use: (client: OkraEffectClient) => Effect.Effect<A, unknown, never>) =>
    runOkraClient(options, use, 'Billing request failed');

  return {
    getBillingUsage: () =>
      run((client) => client.billing.getBillingUsage()).then(normalizeUsage),
    createCheckoutSession: ({ amountCents }) =>
      run((client) => client.billing.createCheckoutSession({ payload: { amountCents } })),
    getTopupSession: ({ sessionId }) =>
      run((client) =>
        client.billing.getTopupSession({ query: { session_id: sessionId } }),
      ).then(normalizeTopup),
    createCardSetupSession: () => run((client) => client.billing.createCardSetupSession()),
    getCardSetupSession: ({ sessionId }) =>
      run((client) =>
        client.billing.getCardSetupSession({ query: { session_id: sessionId } }),
      ).then((wire) => ({ ...wire })),
  };
}
